/**
 * Dashboard quick actions component - Mobile-first design
 */
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';

interface ActionButtonProps {
  icon: string;
  title: string;
  onClick: () => void;
  color: 'blue' | 'green' | 'purple' | 'orange' | 'pink' | 'red';
}

function MobileActionButton({ icon, title, onClick }: ActionButtonProps) {
  return (
    <button
      onClick={onClick}
      className="bg-surface border border-hairline text-ink rounded-xl p-6 hover:border-accent/60 transition-colors flex flex-col items-center justify-center gap-3 min-h-[140px] w-full"
    >
      <span className="text-4xl">{icon}</span>
      <span className="font-semibold text-base text-center leading-tight">{title}</span>
    </button>
  );
}

export function QuickActions() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { customClaims } = useAuth();

  const canManageFranchises = customClaims?.role === 'super_admin';
  const canManageBranches = customClaims && ['super_admin', 'franchise_owner', 'admin'].includes(customClaims.role);
  const isBarber = customClaims?.role === 'barber';
  const isClient = customClaims && ['client', 'guest'].includes(customClaims.role);

  const adminActions = [
    {
      icon: '🏢',
      title: t('dashboard.quickActions.newFranchise'),
      onClick: () => navigate('/franchises/new'),
      color: 'blue' as const,
      show: canManageFranchises,
    },
    {
      icon: '📍',
      title: t('dashboard.quickActions.newBranch'),
      onClick: () => navigate('/branches/new'),
      color: 'green' as const,
      show: canManageBranches,
    },
    {
      icon: '💈',
      title: t('dashboard.quickActions.newBarber'),
      onClick: () => navigate('/admin/barbers'),
      color: 'purple' as const,
      show: canManageBranches,
    },
    {
      icon: '✂️',
      title: t('dashboard.quickActions.newService'),
      onClick: () => navigate('/services/new'),
      color: 'orange' as const,
      show: canManageBranches,
    },
    {
      icon: '🎉',
      title: t('dashboard.quickActions.newOffer'),
      onClick: () => navigate('/offers/new'),
      color: 'pink' as const,
      show: canManageBranches,
    },
    {
      icon: '🎫',
      title: t('dashboard.quickActions.viewQueue'),
      onClick: () => navigate('/queue'),
      color: 'red' as const,
      show: canManageBranches,
    },
  ];

  const barberActions = [
    {
      icon: '💈',
      title: t('dashboard.quickActions.myQueue'),
      onClick: () => navigate('/barber-queue'),
      color: 'purple' as const,
      show: isBarber,
    },
    {
      icon: '👤',
      title: t('dashboard.quickActions.myProfile'),
      onClick: () => navigate('/profile'),
      color: 'blue' as const,
      show: isBarber,
    },
  ];

  const clientActions = [
    {
      icon: '🎫',
      title: t('dashboard.quickActions.takeTicket'),
      onClick: () => navigate('/take-ticket'),
      color: 'green' as const,
      show: isClient,
    },
    {
      icon: '🎟️',
      title: t('dashboard.quickActions.myTickets'),
      onClick: () => navigate('/client-queue'),
      color: 'purple' as const,
      show: isClient,
    },
    {
      icon: '👤',
      title: t('dashboard.quickActions.myProfile'),
      onClick: () => navigate('/profile'),
      color: 'blue' as const,
      show: isClient,
    },
  ];

  const visibleActions = [...adminActions, ...barberActions, ...clientActions].filter(action => action.show);

  if (visibleActions.length === 0) return null;

  return (
    <div className="mb-8">
      <h2 className="text-xl md:text-2xl font-bold text-ink mb-4 px-1">
        {t('dashboard.quickActions.title')}
      </h2>

      {/* Mobile: 2 columns */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 md:gap-4">
        {visibleActions.map((action, index) => (
          <MobileActionButton key={index} {...action} />
        ))}
      </div>
    </div>
  );
}
